import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";
import { ChefHat, Plus, Trash2, Edit, Calculator, ShoppingCart, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
const emptyMenu = { nombre: "", descripcion: "", precio_por_persona: 0, porciones_base: 50, platillos: [], notas: "" };

export default function MenusTab({ unidadSlug, color }) {
  const [menus, setMenus] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMenu);

  const [faltOpen, setFaltOpen] = useState(false);
  const [faltMenu, setFaltMenu] = useState(null);
  const [faltPorciones, setFaltPorciones] = useState(50);
  const [faltData, setFaltData] = useState(null);
  const [creandoOrden, setCreandoOrden] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?unidad_negocio=${unidadSlug}`;
      const [m, r] = await Promise.all([
        axios.get(`${API}/menus${q}`),
        axios.get(`${API}/recetas${q}`),
      ]);
      setMenus((m.data || []).filter((x) => x.nombre?.trim()));
      setRecetas(r.data || []);
    } catch { toast.error("Error al cargar menús"); }
    finally { setLoading(false); }
  }, [unidadSlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm(emptyMenu); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...emptyMenu, ...m, platillos: m.platillos || [] }); setOpen(true); };

  const handleSave = async () => {
    if (!form.nombre?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = {
        ...form,
        unidad_negocio: unidadSlug,
        porciones_base: Number(form.porciones_base) || 50,
        precio_por_persona: Number(form.precio_por_persona) || 0,
      };
      if (editing) {
        await axios.put(`${API}/menus/${editing.menu_id}`, payload);
        toast.success("Menú actualizado");
      } else {
        await axios.post(`${API}/menus`, payload);
        toast.success("Menú creado");
      }
      setOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar menú?")) return;
    try {
      await axios.delete(`${API}/menus/${id}`);
      toast.success("Eliminado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const togglePlatillo = (r) => {
    const has = (form.platillos || []).find((p) => p.receta_id === r.receta_id);
    if (has) {
      setForm({ ...form, platillos: form.platillos.filter((p) => p.receta_id !== r.receta_id) });
    } else {
      setForm({ ...form, platillos: [...(form.platillos || []), { receta_id: r.receta_id, nombre: r.nombre, porciones: form.porciones_base || 50 }] });
    }
  };

  const updPlatilloPorciones = (rid, val) => {
    setForm({
      ...form,
      platillos: form.platillos.map((p) => p.receta_id === rid ? { ...p, porciones: Number(val) || 0 } : p),
    });
  };

  const openFaltantes = (m) => {
    setFaltMenu(m);
    setFaltPorciones(m.porciones_base || 50);
    setFaltData(null);
    setFaltOpen(true);
  };

  const calcularFaltantes = async () => {
    if (!faltMenu) return;
    try {
      const r = await axios.get(`${API}/menus/${faltMenu.menu_id}/faltantes?porciones=${faltPorciones}`);
      setFaltData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const generarOrdenCompra = async () => {
    if (!faltMenu) return;
    setCreandoOrden(true);
    try {
      const r = await axios.post(`${API}/menus/${faltMenu.menu_id}/orden-compra`, {
        porciones: faltPorciones,
        unidad_negocio: unidadSlug,
        notas: `Orden generada desde menú ${faltMenu.nombre} para ${faltPorciones} porciones`,
      });
      if (r.data.orden_id) {
        toast.success(`Orden ${r.data.orden_id} generada · ${r.data.lineas_count} líneas · ${fmt(r.data.total)}`);
      } else {
        toast.info(r.data.mensaje || "No hay faltantes");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error al generar orden"); }
    finally { setCreandoOrden(false); }
  };

  return (
    <div className="space-y-4" data-testid={`menus-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Menús</h2>
          <p className="text-stone-500 text-sm mt-1">Combinaciones de recetas · calcula faltantes vs inventario y genera órdenes de compra.</p>
        </div>
        <Button onClick={openNew} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-nuevo-menu">
          <Plus className="w-4 h-4 mr-2" /> Nuevo menú
        </Button>
      </div>

      {loading ? (
        <div className="text-stone-500 text-sm">Cargando...</div>
      ) : menus.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-8 text-center text-stone-500">
            <ChefHat className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-sm">Sin menús registrados.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {menus.map((m) => (
            <Card key={m.menu_id} className="border-stone-200" data-testid={`card-menu-${m.menu_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg text-stone-900 truncate">{m.nombre}</h3>
                    {m.descripcion && <p className="text-xs text-stone-500 truncate">{m.descripcion}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openFaltantes(m)} title="Faltantes" data-testid={`btn-faltantes-${m.menu_id}`}><Calculator className="w-4 h-4 text-blue-700" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(m.menu_id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                </div>
                <p className="text-xs text-stone-600">{(m.platillos || []).length} recetas · base: {m.porciones_base || "—"} porciones</p>
                {m.precio_por_persona > 0 && <p className="text-sm font-semibold" style={{ color }}>{fmt(m.precio_por_persona)} / persona</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-menu">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar menú" : "Nuevo menú"}</DialogTitle>
            <DialogDescription className="text-xs">Selecciona recetas y define porciones por platillo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="menu-nombre" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea rows={2} value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Porciones base</Label>
                <Input type="number" min="1" value={form.porciones_base} onChange={(e) => setForm({ ...form, porciones_base: Number(e.target.value) || 0 })} data-testid="menu-porciones" />
              </div>
              <div>
                <Label>Precio / persona</Label>
                <Input type="number" step="0.01" value={form.precio_por_persona || 0} onChange={(e) => setForm({ ...form, precio_por_persona: Number(e.target.value) || 0 })} />
              </div>
            </div>
            <div>
              <Label>Recetas incluidas ({(form.platillos || []).length})</Label>
              {recetas.length === 0 ? (
                <p className="text-xs text-stone-500 mt-2">No hay recetas. Crea recetas primero en el tab &quot;Recetas&quot;.</p>
              ) : (
                <div className="mt-2 max-h-64 overflow-y-auto border border-stone-200 rounded-lg">
                  {recetas.map((r) => {
                    const sel = (form.platillos || []).find((p) => p.receta_id === r.receta_id);
                    return (
                      <div key={r.receta_id} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-b-0">
                        <input type="checkbox" checked={!!sel} onChange={() => togglePlatillo(r)} className="w-4 h-4" data-testid={`menu-receta-${r.receta_id}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{r.nombre}</p>
                          <p className="text-[10px] text-stone-500">{r.categoria} · {r.porciones_base} porciones base</p>
                        </div>
                        {sel && (
                          <div className="flex items-center gap-1">
                            <Label className="text-[10px] text-stone-500">Porciones:</Label>
                            <Input type="number" min="1" value={sel.porciones} onChange={(e) => updPlatilloPorciones(r.receta_id, e.target.value)} className="w-20 h-7" data-testid={`platillo-porc-${r.receta_id}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-guardar-menu">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog faltantes / orden compra */}
      <Dialog open={faltOpen} onOpenChange={setFaltOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-faltantes">
          <DialogHeader>
            <DialogTitle>Faltantes de inventario — {faltMenu?.nombre}</DialogTitle>
            <DialogDescription className="text-xs">Calcula ingredientes requeridos vs stock actual y genera una orden de compra.</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Porciones del evento</Label>
              <Input type="number" min="1" value={faltPorciones} onChange={(e) => setFaltPorciones(Number(e.target.value) || 1)} data-testid="falt-porciones" />
            </div>
            <Button onClick={calcularFaltantes} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-calcular-faltantes">
              <Calculator className="w-4 h-4 mr-2" /> Calcular
            </Button>
          </div>

          {faltData && (
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-stone-50 rounded-lg p-3"><p className="text-xs text-stone-500">Ingredientes</p><p className="font-display text-2xl">{faltData.ingredientes.length}</p></div>
                <div className="bg-amber-50 rounded-lg p-3"><p className="text-xs text-stone-500">Faltantes</p><p className="font-display text-2xl text-amber-700">{faltData.total_faltantes}</p></div>
                <div className="rounded-lg p-3" style={{ background: `${color}15` }}><p className="text-xs text-stone-500">Compra estimada</p><p className="font-display text-2xl" style={{ color }}>{fmt(faltData.costo_estimado_compra)}</p></div>
              </div>

              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead><tr className="bg-stone-50 text-xs text-stone-500 text-left sticky top-0">
                    <th className="p-2">Producto</th><th className="p-2 text-right">Req.</th><th className="p-2 text-right">Stock</th><th className="p-2 text-right">Falta</th><th className="p-2 text-right">$/unid</th><th className="p-2 text-right">Subtotal</th><th className="p-2">Origen</th>
                  </tr></thead>
                  <tbody>
                    {faltData.ingredientes.map((f, idx) => (
                      <tr key={idx} className={`border-t border-stone-100 ${f.faltante > 0 ? "bg-amber-50/40" : ""}`} data-testid={`falt-row-${idx}`}>
                        <td className="p-2">{f.producto} <span className="text-xs text-stone-400">({f.unidad})</span></td>
                        <td className="p-2 text-right">{f.requerido}</td>
                        <td className="p-2 text-right">{f.stock_actual}</td>
                        <td className={`p-2 text-right font-semibold ${f.faltante > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                          {f.faltante > 0 ? <><AlertTriangle className="w-3 h-3 inline mr-1" />{f.faltante}</> : <CheckCircle2 className="w-3 h-3 inline" />}
                        </td>
                        <td className="p-2 text-right">{fmt(f.costo_unitario)}</td>
                        <td className="p-2 text-right">{f.faltante > 0 ? fmt(f.costo_faltante) : "—"}</td>
                        <td className="p-2 text-xs text-stone-500">{f.origen_receta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button
                onClick={generarOrdenCompra}
                disabled={faltData.total_faltantes === 0 || creandoOrden}
                className="w-full"
                style={{ background: color }}
                data-testid="btn-generar-orden"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {creandoOrden ? "Generando…" : `Generar orden de compra (${faltData.total_faltantes} faltantes · ${fmt(faltData.costo_estimado_compra)})`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
